<template>
	<div id="planner-app" class="bg-blue-100 pb-4" :key="props.keyUpdater">
		<Overlay
			:position="'absolute'"
			:open="store.state.loading"
			:color="''"
			:maxOpacity="1"
			:contentTimeout="110"
			:classes="'loader'"
		>
			<div v-if="store.state.loading">
				<!-- <div class="loader position-fixed"></div> -->
			</div>
		</Overlay>

		<div v-if="store.state.productUnavailable && !store.state.activePrestations">
			<OopsBanner />
		</div>
		<div
			v-else
			id="planner-main-wrapper"
			class="container px-0 px-md-3 bg-blue-100"
		>
			<div v-if="!store.state.activePrestations" class="col-12 px-0">
				<div class="container px-0 text-center">
					<h2>
						<span class="title-2 text-grey-800">{{
							store.state.content.planner_planVacation
						}}</span>
					</h2>
				</div>
			</div>
			<template v-if="hasTransportOptions">
				<TransportSwitch v-if="!store.state.activePrestations"></TransportSwitch>
				<TNETimeLine v-if="showTrainTimeLine" />
				<EurostarTimeLine v-if="showEurostarTimeLine" />
			</template>
			<Search v-if="!store.state.activePrestations"></Search>
			<Result></Result>
			<Summary v-if="!store.state.activePrestations"></Summary>
		</div>

		<ErrorMessage :text="errorText" @close="handleErrorMessageClose" />
	</div>
</template>

<script lang="ts" setup>
import { computed, ComputedRef, onMounted, watch } from "vue";
//import store from "@/vue/planning/store/store";
import documentReady from "@/vue/planning/logic/documentReady";

// components
import Search from "@/vue/planning/components/search.vue";
import Result from "@/vue/planning/components/result.vue";
import Summary from "@/vue/planning/components/summary.vue";
import TransportSwitch from "@/vue/planning/components/transportSwitch.vue";
import TNETimeLine from "@/vue/planning/components/TNETimeLine.vue";
import EurostarTimeLine from "@/vue/planning/components/EurostarTimeLine.vue";
import OopsBanner from "@/vue/planning/components/oopsBanner.vue";
import Overlay from "@/vue/common/overlay.vue";
import ErrorMessage from "@/vue/planning/components/common/errorMessage.vue";

// computed
import hasTransportOptions from "@/vue/planning/computed/hasTransportOptions";
import flightPackCode from "@/config/flightPackCode";
import { isTneOrigin, isEurostarOrigin, isNativeTnePack, isNativeEurostarPack, resolveTrainPackCodeForOrigin, variantBelongsToPackCode } from "@/config/trainPackageConfig";

import { useStore } from "vuex";
const store = useStore();


interface Props {
	keyUpdater: string;
}
const props = defineProps<Props>();

const TNE_TIMELINE_PACKS = ["PFTTF", "PFPTTF", "PFTTUK", "PFPTTUK", "PFTTNL", "PFPTTNL", "HTTF"];
const EUROSTAR_BANNER_PACKS = ["PFTT", "PFPTT", "HTT"];

const packMatches = (packCode: string, prefixes: string[]) =>
	prefixes.some((code) => variantBelongsToPackCode(packCode, code));

const originCode = computed(() => store.state.flightOrigin?.split("-")[0] || "");

const resolvedTrainPackCode = computed(() =>
	resolveTrainPackCodeForOrigin(store.state.packCode || "", originCode.value)
);

const showTrainTimeLine = computed(() => {
	// AMS/BRU are flight airports and TNE stations. Flight packs must not show a train banner.
	if (flightPackCode.includes(store.state.packCode || "")) return false;
	if (isEurostarOrigin(originCode.value)) return false;
	if (isTneOrigin(originCode.value)) return true;

	const packCode = store.state.packCode || "";
	const resolved = resolvedTrainPackCode.value;
	if (isNativeTnePack(packCode) || isNativeTnePack(resolved)) return true;
	return packMatches(packCode, TNE_TIMELINE_PACKS) || packMatches(resolved, TNE_TIMELINE_PACKS);
});

const showEurostarTimeLine = computed(() => {
	if (flightPackCode.includes(store.state.packCode || "")) return false;
	if (showTrainTimeLine.value) return false;
	if (isEurostarOrigin(originCode.value)) return true;

	const packCode = store.state.packCode || "";
	const resolved = resolvedTrainPackCode.value;
	if (isNativeEurostarPack(packCode) || isNativeEurostarPack(resolved)) return true;
	return packMatches(packCode, EUROSTAR_BANNER_PACKS) || packMatches(resolved, EUROSTAR_BANNER_PACKS);
});

// computed
const errorText: ComputedRef<string> = computed(() => {
	const val = store.state.content[store.state.errorMessageOpen];
	return val;
});


// methods
const handleErrorMessageClose = () => {
	store.commit("setErrorMessageOpen", "");
};

onMounted(async () => {
	await documentReady(store);
	window.addEventListener("closeMonCompteModal", async () => {
		await documentReady(store);
	});

	store.commit("setScreenType", window.screenSizeManager.screenType);
	window.addEventListener("screenTypeChange", () => {
		store.commit("setScreenType", window.screenSizeManager.screenType);
	});

	const triggerResize = () => {
		window.dispatchEvent(new Event('resize'));
	};

	triggerResize(); // initial
	window.addEventListener('orientationchange', triggerResize);
	// window.addEventListener('resize', triggerResize);
});

// watch
watch(
	() => store.state.bodyOverflowHidden,
	(newValue: boolean) => {
		if (newValue) {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-hidden");
		}
	}
);
</script>

<style scoped>
@media (max-width: 1500px) {
	#planner-main-wrapper{
		max-width: 1200px;
	}
}
</style>
